import React from 'react';
import { Card, CardContent, Typography, Box, Chip, Divider, List, ListItem, ListItemIcon, ListItemText, CircularProgress, Alert, Stack, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InsightsIcon from '@mui/icons-material/Insights';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import { LineChart } from '@mui/x-charts/LineChart';
import { renderRichText } from '../utils/richText';

/**
 * AIInsights - the single, unified AI result surface.
 *
 * Both AI features render through this one component so they look identical (the
 * "merge into one AI view" requirement). It OWNS its input coercion: callers pass
 * the raw agent fields (which may be missing or the wrong type) and this component
 * normalizes them, so a second caller never has to re-implement the guards.
 *
 * @param {Object} props
 * @param {boolean} props.visible
 * @param {boolean} props.loading
 * @param {string}  props.error
 * @param {string}  props.headline     - recommendation text or dashboard summary
 * @param {*}       props.trend
 * @param {*}       props.insights      - expected string[]; coerced defensively
 * @param {*}       props.focusAreas    - expected string[]; coerced defensively
 * @param {*}       props.dataPoints    - expected [{timestamp, bmi, category}]; coerced
 * @param {string}  props.disclaimer
 * @param {number|null} props.remainingFree
 * @param {string}  props.model
 * @param {string}  props.loadingText
 */
function AIInsights({
  visible, loading, error, headline, trend, insights, focusAreas,
  dataPoints, disclaimer, remainingFree, model, loadingText,
}) {
  if (!visible) {
    return null;
  }

  // --- input coercion (owned here, not at the call site) ---
  const headlineText = headline != null ? String(headline) : '';
  const trendText = trend != null ? String(trend) : '';
  const insightList = Array.isArray(insights) ? insights.map(String) : [];
  const focusList = Array.isArray(focusAreas) ? focusAreas.map(String) : [];
  const points = Array.isArray(dataPoints) ? dataPoints : [];

  // Chart needs finite timestamp AND bmi; the table shows every record (so points
  // lacking a timestamp, and the per-record category, are never silently dropped).
  const chartPoints = points
    .map((d) => ({ ts: Number(d && d.timestamp), bmi: Number(d && d.bmi) }))
    .filter((d) => Number.isFinite(d.ts) && Number.isFinite(d.bmi))
    .sort((a, b) => a.ts - b.ts);

  return (
    <Card elevation={3} sx={{ mt: 3, overflow: 'hidden' }} id="ai-insights">
      <Box sx={{ px: 3, py: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesomeIcon />
        <Typography variant="h3" component="h2" sx={{ color: '#fff' }}>AI Insights</Typography>
      </Box>

      <CardContent>
        {loading && (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={22} />
            <Typography color="text.secondary">{loadingText || 'Generating your personalized insights…'}</Typography>
          </Stack>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && (
          <>
            {headlineText && (
              <Box sx={{ mb: 2 }} className="ai-headline">{renderRichText(headlineText)}</Box>
            )}

            {trendText && (
              <Box sx={{ mb: 2 }}>
                <Chip color="secondary" variant="outlined" label={`Trend: ${trendText}`} />
              </Box>
            )}

            {chartPoints.length > 0 && (
              <Box sx={{ my: 2 }}>
                <Typography variant="h3" gutterBottom>BMI Over Time</Typography>
                <LineChart
                  height={260}
                  xAxis={[{
                    data: chartPoints.map((p) => new Date(p.ts * 1000)),
                    scaleType: 'time',
                    valueFormatter: (v) => new Date(v).toLocaleDateString(),
                  }]}
                  series={[{ data: chartPoints.map((p) => p.bmi), label: 'BMI', color: '#667eea', area: true, showMark: true }]}
                />
              </Box>
            )}

            {insightList.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h3" gutterBottom>Insights</Typography>
                <List dense>
                  {insightList.map((it, i) => (
                    <ListItem key={i} disableGutters alignItems="flex-start">
                      <ListItemIcon sx={{ minWidth: 34, mt: 0.5 }}><InsightsIcon color="primary" fontSize="small" /></ListItemIcon>
                      {/* richText so agent markdown (**bold**, lists) doesn't print literally;
                          component="div" avoids nesting <p>/<ul> inside ListItemText's default <p> */}
                      <ListItemText primary={renderRichText(it)} primaryTypographyProps={{ component: 'div' }} />
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {focusList.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h3" gutterBottom>Focus Areas</Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {focusList.map((it, i) => (
                    <Chip key={i} icon={<CenterFocusStrongIcon />} label={it} color="primary" variant="outlined" />
                  ))}
                </Stack>
              </>
            )}

            {points.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h3" gutterBottom>Recent Records</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow><TableCell>When</TableCell><TableCell>BMI</TableCell><TableCell>Category</TableCell></TableRow>
                  </TableHead>
                  <TableBody>
                    {points.map((d, i) => {
                      const ts = Number(d && d.timestamp);
                      const bmi = Number(d && d.bmi);
                      return (
                        <TableRow key={i}>
                          <TableCell>{Number.isFinite(ts) ? new Date(ts * 1000).toLocaleDateString() : '--'}</TableCell>
                          <TableCell>{Number.isFinite(bmi) ? bmi : '--'}</TableCell>
                          <TableCell>{d && d.category ? String(d.category) : '--'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </>
            )}

            {disclaimer && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>{disclaimer}</Typography>
            )}
            {typeof remainingFree === 'number' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Free AI uses left this month: {remainingFree}
              </Typography>
            )}
            {model && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Generated by {model}</Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default AIInsights;
